import type { AuditCategory, AuditEventView, AuditOutcome, BooAccessEvent, BooAccessEventKind, BooPerson, SsoAuditEntry } from "@/lib/api";
import { describeAccessEvent } from "../components/boo/access-copy.ts";
import { ssoChangeSummary } from "./sso-audit.ts";

// The Audit log's words: one sentence per action in boo-backend's catalog (src/lib/audit/catalog.ts),
// the actor wording, and the filter labels. No React and no api client at
// runtime, so `node --test` covers it. User-visible copy follows the product
// voice: "Boo", never "AI" / "agent" / "container".

export const CATEGORY_LABEL: Record<AuditCategory, string> = {
  access: "Boo access",
  members: "Members",
  sign_in: "Sign-in",
  sso: "App sign-in",
  connectors: "Connectors",
  integrations: "Integrations",
  secrets: "Secrets",
  spending: "Spending limits",
  org: "Organization settings",
  billing: "Billing",
  staff: "Boo Lab support",
  boo_activity: "Boo activity",
};

export const AUDIT_CATEGORIES = Object.keys(CATEGORY_LABEL) as AuditCategory[];

// A Boo's own activity never reaches the org log (boo-backend leaves it out), so the org page doesn't offer it.
export const ORG_AUDIT_CATEGORIES = AUDIT_CATEGORIES.filter((c) => c !== "boo_activity");

// What a Boo's Activity tab offers: the categories its events are filed under. Org-wide
// ones (members, sign-in, settings, billing) never name a Boo.
export const BOO_AUDIT_CATEGORIES: AuditCategory[] = ["boo_activity", "access", "connectors", "integrations", "sso", "secrets", "spending", "staff"];

export function isAuditCategory(v: unknown): v is AuditCategory {
  return typeof v === "string" && v in CATEGORY_LABEL;
}

export const OUTCOME_LABEL: Record<AuditOutcome, string> = {
  success: "Succeeded",
  denied: "Denied",
  error: "Failed",
};

export const VIA_LABEL: Record<string, string> = {
  dashboard: "Dashboard",
  chat: "Chat",
  api: "API",
  automation_key: "Boo Lab automation",
  admin: "Boo Lab support console",
  system: "Automatic",
};

const PLATFORM: Record<string, string> = {
  slack: "Slack",
  teams: "Teams",
  gchat: "Google Chat",
  discord: "Discord",
  sms: "SMS",
  imessage: "iMessage",
  dashboard_chat: "dashboard chat",
};

// Same words as ORG_ROLE_LABEL (org-roles.ts, which pulls in icons).
const ORG_ROLE: Record<string, string> = { owner: "Owner", admin: "Admin", billing_admin: "Billing admin", member: "Read-only" };

const SIGN_IN_METHOD: Record<string, string> = { email_otp: "an email code", google: "Google", microsoft: "Microsoft" };

const SIGN_IN_FAILURE: Record<string, string> = {
  invalid_code: "the code was wrong",
  expired_code: "the code had expired",
  too_many_attempts: "too many tries",
  banned: "the account is suspended",
  other: "something went wrong",
};

// What a Boo Lab support engineer did in the dashboard (staff.dashboard.acted, `details.action`).
const STAFF_DASHBOARD: Record<string, (boo: string) => string> = {
  dashboard_chat: (boo) => `opened ${boo}'s dashboard chat`,
  roles_changed: (boo) => `changed what people can use in ${boo}`,
  invite_access_removed: (boo) => `took back an invite to ${boo}`,
  person_changed: (boo) => `changed a person's details in ${boo}`,
  group_added: (boo) => `added a person to a group in ${boo}`,
  group_removed: (boo) => `removed a person from a group in ${boo}`,
  handle_linked: (boo) => `linked a chat account in ${boo}`,
  handle_unlinked: (boo) => `separated a chat account in ${boo}`,
  storage_opened: (boo) => `opened ${boo}'s stored data`,
};

const SETTING_LABEL: Record<string, { name: string; values?: Record<string, string> }> = {
  connectorSetup: { name: "who can set up connectors", values: { everyone: "everyone", admins: "admins only" } },
};

// Every Boo access kind the backend records as boo.access.<kind>; super_admin_access became staff.dashboard.acted.
export const ACCESS_KINDS: Record<Exclude<BooAccessEventKind, "super_admin_access">, true> = {
  role_granted: true,
  role_changed: true,
  role_removed: true,
  ownership_taken: true,
  owner_assigned: true,
  deleted_by_org_owner: true,
  workspace_attached: true,
  workspace_released: true,
  link_requested: true,
  link_approved: true,
  link_declined: true,
  link_revoked: true,
  link_more_requested: true,
  link_more_approved: true,
  link_more_declined: true,
  link_grant_changed: true,
  link_removal_confirmed: true,
  link_expired: true,
  link_admins_asked: true,
  boo_handed_over: true,
  memories_archived: true,
  roles_changed: true,
  person_blocked: true,
  person_unblocked: true,
  view_all_spend_granted: true,
  view_all_spend_revoked: true,
  group_added: true,
  group_removed: true,
  groups_kept: true,
  invite_access_removed: true,
  handle_linked: true,
  handle_unlinked: true,
};

const ACCESS_PREFIX = "boo.access.";

// ── Small readers ───────────────────────────────────────────────────────────

type Meta = Record<string, unknown>;

const text = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() && Number.isFinite(Number(v)) ? Number(v) : null);
const obj = (v: unknown): Meta => (v && typeof v === "object" && !Array.isArray(v) ? (v as Meta) : {});
const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && !!x) : []);
const meta = (e: AuditEventView): Meta => e.metadata ?? {};

const money = (v: number) => `$${Math.abs(v).toFixed(2)}`;

function listText(items: string[], max = 3): string {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length <= max) return `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`;
  return `${items.slice(0, max).join(", ")} and ${items.length - max} more`;
}

function booName(e: AuditEventView): string {
  return e.boo?.name || (e.boo ? "a deleted Boo" : "a Boo");
}

function targetName(e: AuditEventView, fallback: string): string {
  return e.target?.label || e.target?.id || fallback;
}

function platformOf(e: AuditEventView): string | null {
  const m = meta(e);
  const key = text(m.actorProvider) ?? text(m.provider) ?? (e.actor.id?.includes(":") ? e.actor.id.split(":")[0] : null);
  return key ? (PLATFORM[key] ?? null) : null;
}

/**
 * Who did it, in the log's words:
 * staff "Boo Lab support (email)", agent_key "Boo Lab automation (label)",
 * boo the Boo's name, person the display name and the chat app.
 */
export function auditActorLabel(e: AuditEventView): string {
  const a = e.actor;
  switch (a.type) {
    case "staff": {
      // Rows copied from older history carry the engineer's user id, not an email; never show an id.
      const email = [a.email, a.label, a.id].find((v) => !!v && /^[^@\s]+@[^@\s]+$/.test(v));
      return email ? `Boo Lab support (${email})` : "Boo Lab support";
    }
    case "agent_key":
      return `Boo Lab automation (${a.label || a.id || "a key"})`;
    case "boo":
      return a.label || e.boo?.name || "Boo";
    case "person": {
      const app = platformOf(e);
      const name = a.label || a.id || "Someone";
      return app ? `${name} (${app})` : name;
    }
    case "system":
      return "Automatic process";
    default:
      return a.label || a.email || "Someone";
  }
}

// The actor as a sentence's subject.
function subject(e: AuditEventView): string {
  return e.actor.type === "system" ? "An automatic process" : auditActorLabel(e);
}

// ── Sentences ───────────────────────────────────────────────────────────────

type Copy = (e: AuditEventView, who: string) => string;

function accessSentence(e: AuditEventView): string {
  const m = meta(e);
  const kind = (text(m.kind) ?? e.action.slice(ACCESS_PREFIX.length)) as BooAccessEventKind;
  const asPerson = (id: string | null, name: string | null, email: string | null): BooPerson => ({ userId: id ?? "", name, email });
  const actor: BooPerson =
    e.actor.type === "user" ? asPerson(e.actor.id, e.actor.label, e.actor.email) : asPerson(e.actor.id, subject(e), null);
  const target = e.target && e.target.type !== "boo" ? asPerson(e.target.id, e.target.label, text(m.targetEmail)) : null;
  const event: BooAccessEvent = {
    id: e.id,
    kind,
    actor,
    target,
    role: text(m.role),
    previousRole: text(m.previousRole),
    reason: e.reason,
    details: obj(m.details),
    notice: false,
    createdAt: e.createdAt,
  };
  return describeAccessEvent(event, booName(e));
}

function signInMethod(e: AuditEventView): string {
  const method = text(meta(e).method);
  return method ? (SIGN_IN_METHOD[method] ?? method.replace(/_/g, " ")) : "their usual method";
}

function orgRole(v: unknown): string {
  const role = text(v);
  return role ? (ORG_ROLE[role] ?? role.replace(/_/g, " ")) : "a member";
}

function memberName(e: AuditEventView): string {
  return targetName(e, "someone");
}

function ssoSentence(e: AuditEventView, who: string): string {
  const event = e.action.slice("app.sso.".length);
  const app = e.target?.label || e.target?.id;
  switch (event) {
    case "settings_updated":
      return `${who} changed ${booName(e)}'s sign-in options.`;
    case "settings_reset":
      return `${who} put ${booName(e)}'s sign-in options back to the suggested ones.`;
    case "policy_updated":
      return `${who} changed who can sign in to ${app ?? "an app"}.`;
    case "policy_reset":
      return `${who} put who can sign in to ${app ?? "an app"} back to the default.`;
    case "app_updated":
      return `${who} changed the sign-in page of ${app ?? "an app"}.`;
    case "client_registered":
      return `${who} set up sign-in for ${app ?? "a new app"}.`;
    case "ownership_taken":
      return `${who} moved ${app ?? "an app"} to ${booName(e)}.`;
    default:
      return `${who} changed app sign-in.`;
  }
}

function connectorName(e: AuditEventView): string {
  return targetName(e, "a connector");
}

function connectorSentence(e: AuditEventView, who: string): string {
  const m = meta(e);
  const c = connectorName(e);
  const action = text(m.action);
  const kind = e.action.slice("connector.".length);
  if (kind === "approval") {
    const what = action ?? "an action";
    switch (text(m.outcome)) {
      case "requested":
        return `${c} asked a person before running ${what}.`;
      case "approved":
        return `${who} approved ${what} on ${c}.`;
      case "denied":
        return `${who} declined ${what} on ${c}.`;
      case "action_off":
        return `${who} approved ${what} on ${c}, but it had been turned off.`;
      default:
        return `${who} answered a request to run ${what} on ${c}.`;
    }
  }
  if (kind === "connection") {
    switch (action) {
      case "verify":
        return `${who} tested ${c}'s connection.`;
      case "disconnect":
        return `${who} removed ${c}'s connection.`;
      case "link_created":
        return `${who} made a connection link for ${c}.`;
      case "links_revoked":
        return `${who} ended unused connection links for ${c}.`;
      case "purge":
        return `${c}'s variables were deleted along with its Boo.`;
      default:
        return `${who} changed ${c}'s connection.`;
    }
  }
  if (kind === "code") return action?.startsWith("git_") ? `${who} got a credential for ${c}'s code.` : `${who} changed ${c}'s code.`;
  if (kind === "variable") return `${who} changed ${c}'s variables.`;
  return `${who} changed ${c}'s settings.`;
}

function appName(e: AuditEventView): string {
  return text(meta(e).appName) ?? e.target?.label ?? text(meta(e).appSlug) ?? "an app";
}

function storagePath(e: AuditEventView): string {
  const m = meta(e);
  return text(m.path) ?? e.target?.label ?? "a file";
}

function secretPath(e: AuditEventView): string {
  const m = meta(e);
  const mount = text(m.mount);
  const path = text(m.path);
  if (mount && path) return `${mount.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
  return path ?? e.target?.label ?? "a secret";
}

function spendTarget(e: AuditEventView): string {
  if (e.target?.label) return e.target.label;
  const key = text(meta(e).targetKey);
  if (!key || key === "org") return "the organization";
  if (key.startsWith("group:")) return "a budget group";
  if (key.startsWith("boo:")) return "a Boo";
  return "a person";
}

function limitAmounts(after: Meta): string {
  const daily = num(after.dailyUsd);
  const monthly = num(after.monthlyUsd);
  const parts = [daily !== null ? `${money(daily)} a day` : null, monthly !== null ? `${money(monthly)} a month` : null].filter(Boolean);
  return parts.length ? ` to ${parts.join(" and ")}` : "";
}

function spendSentence(e: AuditEventView, who: string): string {
  const m = meta(e);
  const before = obj(m.before);
  const after = obj(m.after);
  const t = spendTarget(e);
  const groupName = (v: Meta) => text(v.name) ?? t;
  const person = text(m.personLabel) ?? "a person";
  switch (e.action) {
    case "spend.limit.set":
      return `${who} set the spending limit for ${t}${limitAmounts(after)}.`;
    case "spend.limit.remove":
      return `${who} removed the spending limit for ${t}.`;
    case "spend.default_warn.set": {
      const pct = num(after.warnPercent);
      return pct !== null ? `${who} set the default warning to ${pct}% of a limit.` : `${who} changed the default warning level.`;
    }
    case "spend.group.create":
      return `${who} created the budget group ${groupName(after)}.`;
    case "spend.group.rename":
      return `${who} renamed the budget group ${groupName(before)} to ${groupName(after)}.`;
    case "spend.group.delete":
      return `${who} deleted the budget group ${groupName(before)}.`;
    case "spend.group.member_add":
      return `${who} added ${person} to the budget group ${t}.`;
    case "spend.group.member_remove":
      return `${who} removed ${person} from the budget group ${t}.`;
    case "spend.grant.create": {
      const amount = num(after.amountUsd);
      return `${who} gave ${t} a one-time extra${amount !== null ? ` ${money(amount)}` : ""}.`;
    }
    case "spend.grant.remove": {
      const amount = num(before.amountUsd);
      return `${who} took back a one-time extra${amount !== null ? ` ${money(amount)}` : ""} from ${t}.`;
    }
    case "spend.request.granted":
      return `${who} approved a one-time increase for ${t}.`;
    case "spend.request.raised":
      return `${who} raised the limit for ${t} after a request.`;
    case "spend.request.denied":
      return `${who} declined a request to spend more for ${t}.`;
    default:
      return `${who} changed spending limits.`;
  }
}

function settingSentence(e: AuditEventView, who: string): string {
  const m = meta(e);
  const key = text(m.setting) ?? e.target?.id ?? "";
  const spec = SETTING_LABEL[key];
  const value = (v: unknown) => {
    const s = text(v) ?? (typeof v === "boolean" ? (v ? "on" : "off") : null);
    return s ? (spec?.values?.[s] ?? s) : "nothing";
  };
  const name = spec?.name ?? e.target?.label ?? (key ? key.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase() : "a setting");
  if (m.from === undefined && m.to === undefined) return `${who} changed ${name}.`;
  return `${who} changed ${name} from ${value(m.from)} to ${value(m.to)}.`;
}

function tablesText(e: AuditEventView): string {
  const tables = strings(meta(e).tables);
  return tables.length ? `the ${listText(tables)} ${tables.length === 1 ? "table" : "tables"}` : "its tables";
}

function downloadText(e: AuditEventView): string {
  const m = meta(e);
  const path = text(m.path);
  if (path) return path;
  const count = num(m.fileCount);
  const roots = strings(m.roots);
  if (count !== null) return `${count} ${count === 1 ? "file" : "files"}${roots.length ? ` from ${listText(roots)}` : ""}`;
  return roots.length ? listText(roots) : "files";
}

function namedThing(e: AuditEventView, noun: string): string {
  const label = e.target?.label;
  return label ? `the ${noun} ${label}` : `a ${noun}`;
}

const COPY: Record<string, Copy> = {
  // ── staff
  "staff.dashboard.acted": (e, who) => {
    const m = meta(e);
    const action = text(obj(m.details).action) ?? text(m.action);
    const phrase = (action && STAFF_DASHBOARD[action]) || ((boo: string) => `made a change in ${boo}`);
    return `${who} ${phrase(booName(e))}.`;
  },
  "staff.org.entered": (_e, who) => `${who} opened the organization.`,
  "staff.self_hosted_boo.created": (e, who) => `${who} set up ${booName(e)} to run on the organization's own machine.`,
  "staff.self_hosted_boo.token_rotated": (e, who) => `${who} made a new token for ${booName(e)}.`,
  "staff.self_hosted_boo.url_changed": (e, who) => `${who} changed the address of ${booName(e)}.`,
  "staff.feedback.read": (e, who) => `${who} read a problem report from ${booName(e)}.`,
  "staff.feedback.reviewed": (e, who) => `${who} reviewed a problem report from ${booName(e)}.`,
  "staff.storage.certificate_issued": (e, who) => `${who} got a pass to open ${booName(e)}'s stored data.`,
  "staff.storage.session_started": (e, who) => `${who} opened ${booName(e)}'s stored data.`,
  "staff.storage.file_opened": (e, who) => `${who} opened ${storagePath(e)} in ${booName(e)}'s stored data.`,
  "staff.storage.downloaded": (e, who) => `${who} downloaded ${downloadText(e)} from ${booName(e)}'s stored data.`,
  "staff.storage.queried": (e, who) => `${who} looked up ${tablesText(e)} in ${booName(e)}'s stored data.`,
  "staff.storage.key_unwrapped": (e) => `${booName(e)} unlocked its stored data for Boo Lab support.`,
  "staff.bundles.railway_token_rotated": (e, who) => `${who} replaced the Railway token ${booName(e)} deploys its apps with.`,
  "automation_key.used": (e, who) => {
    const capability = text(meta(e).capability);
    return `${who} used ${capability ? `its ${capability} access` : "its access"}${e.boo ? ` on ${booName(e)}` : ""}.`;
  },
  "agent_key.created": (e, who) => `${who} created ${namedThing(e, "automation key")}.`,
  "agent_key.scopes_changed": (e, who) => `${who} changed what ${namedThing(e, "automation key")} can do.`,
  "agent_key.revoked": (e, who) => `${who} turned off ${namedThing(e, "automation key")}.`,

  // ── members
  "member.invited": (e, who) => `${who} invited ${memberName(e)} as ${orgRole(meta(e).role)}.`,
  "member.invitation_canceled": (e, who) => `${who} canceled the invitation for ${memberName(e)}.`,
  "member.joined": (e) => `${memberName(e)} joined as ${orgRole(meta(e).role)}.`,
  "member.role_changed": (e, who) => `${who} changed ${memberName(e)} from ${orgRole(meta(e).previousRole)} to ${orgRole(meta(e).role)}.`,
  "member.removed": (e, who) => `${who} removed ${memberName(e)} from the organization.`,
  "member.left": (e) => `${memberName(e)} left the organization.`,

  // ── sign-in
  "auth.signin.succeeded": (e) => `${targetName(e, auditActorLabel(e))} signed in with ${signInMethod(e)}.`,
  "auth.signin.failed": (e) => {
    const reason = text(meta(e).reason);
    const why = reason ? (SIGN_IN_FAILURE[reason] ?? reason.replace(/_/g, " ")) : null;
    return `${targetName(e, auditActorLabel(e))} couldn't sign in with ${signInMethod(e)}${why ? `: ${why}` : ""}.`;
  },
  "auth.signin.blocked": (e) => `${targetName(e, auditActorLabel(e))} was kept from signing in with ${signInMethod(e)} by the organization's sign-in rules.`,

  // ── connectors
  "connector.code": connectorSentence,
  "connector.variable": connectorSentence,
  "connector.setting": connectorSentence,
  "connector.approval": connectorSentence,
  "connector.connection": connectorSentence,
  "connector.call": (e, who) => `${who} ran ${text(meta(e).toolId) ?? "an action"} on ${connectorName(e)}.`,
  "connector.promoted": (e, who) => {
    const m = meta(e);
    const approver = text(m.approver) ?? text(obj(m.approver).name);
    return `${approver ?? who} approved a new version of ${connectorName(e)}.`;
  },

  // ── integrations
  "integration.grant.issued": (e) => `${booName(e)} got access to ${appName(e)}.`,
  "integration.grant.denied": (e) => `${booName(e)} was refused access to ${appName(e)}.`,
  "integration.action.run": (e, who) => `${who} ran ${text(meta(e).toolId) ?? "an action"} in ${appName(e)}.`,
  "integration.connect_link.created": (e, who) => `${who} made a link to connect ${appName(e)}.`,
  "integration.connect_link.completed": (e, who) => `${who} connected ${appName(e)}.`,

  // ── secrets
  "vault.secret.read": (e, who) => `${who} revealed the secret ${secretPath(e)}.`,
  "vault.secret.written": (e, who) => `${who} saved the secret ${secretPath(e)}.`,
  "vault.secret.deleted": (e, who) => `${who} deleted the secret ${secretPath(e)}.`,

  // ── spending limits
  "spend.limit.set": spendSentence,
  "spend.limit.remove": spendSentence,
  "spend.default_warn.set": spendSentence,
  "spend.group.create": spendSentence,
  "spend.group.rename": spendSentence,
  "spend.group.delete": spendSentence,
  "spend.group.member_add": spendSentence,
  "spend.group.member_remove": spendSentence,
  "spend.grant.create": spendSentence,
  "spend.grant.remove": spendSentence,
  "spend.request.granted": spendSentence,
  "spend.request.raised": spendSentence,
  "spend.request.denied": spendSentence,

  // ── org and billing
  "org.setting.changed": settingSentence,
  "billing.credits.granted": (e, who) => {
    const amount = num(meta(e).amount);
    return `${who} added ${amount !== null ? `${money(amount)} in credits` : "credits"}.`;
  },
  "billing.credits.adjusted": (e, who) => {
    const amount = num(meta(e).amount);
    if (amount === null) return `${who} adjusted the credit balance.`;
    return `${who} ${amount < 0 ? "took" : "added"} ${money(amount)} ${amount < 0 ? "off" : "to"} the credit balance.`;
  },

  // ── app sign-in
  "app.sso.settings_updated": ssoSentence,
  "app.sso.settings_reset": ssoSentence,
  "app.sso.policy_updated": ssoSentence,
  "app.sso.policy_reset": ssoSentence,
  "app.sso.app_updated": ssoSentence,
  "app.sso.client_registered": ssoSentence,
  "app.sso.ownership_taken": ssoSentence,

  // ── Boo activity
  "boo.action.approved": (e, who) => `${who} let ${booName(e)} ${e.target?.label ?? "go ahead"}.`,
  "boo.action.denied": (e, who) => (e.target?.label ? `${who} declined to let ${booName(e)} ${e.target.label}.` : `${who} declined a request from ${booName(e)}.`),
  "workflow.launched": (e, who) => `${who} started ${namedThing(e, "workflow")}.`,
  "workflow.paused": (e, who) => `${who} paused ${namedThing(e, "workflow")}.`,
  "workflow.resumed": (e, who) => `${who} resumed ${namedThing(e, "workflow")}.`,
  "workflow.cancelled": (e, who) => `${who} canceled ${namedThing(e, "workflow")}.`,
  "heartbeat.paused": (e, who) => `${who} paused ${namedThing(e, "scheduled task")}.`,
  "heartbeat.resumed": (e, who) => `${who} resumed ${namedThing(e, "scheduled task")}.`,
  "heartbeat.changed": (e, who) => `${who} changed ${namedThing(e, "scheduled task")}.`,
  "daemon.paused": (e, who) => `${who} paused ${namedThing(e, "background task")}.`,
  "daemon.resumed": (e, who) => `${who} resumed ${namedThing(e, "background task")}.`,
  "daemon.changed": (e, who) => `${who} changed ${namedThing(e, "background task")}.`,
  "thread.stopped": (e, who) => {
    const channel = text(meta(e).channel);
    return `${who} stopped ${booName(e)} mid-reply${channel ? ` in ${channel}` : ""}.`;
  },
};

/** True when the action has its own sentence (not the generic fallback). */
export function hasAuditCopy(action: string): boolean {
  if (action.startsWith(ACCESS_PREFIX)) return action.slice(ACCESS_PREFIX.length) in ACCESS_KINDS;
  return action in COPY;
}

/** One plain sentence for an audit event. Unknown actions get a generic one. */
export function describeAuditEvent(e: AuditEventView): string {
  if (e.action.startsWith(ACCESS_PREFIX)) return accessSentence(e);
  const copy = COPY[e.action];
  const who = subject(e);
  if (copy) return copy(e, who);
  const words = e.action.replace(/[._]+/g, " ").trim();
  return `${who} made a change${words ? ` (${words})` : ""}.`;
}

/** A second, quieter line under the sentence: what exactly changed, when the event says. */
export function auditEventSummary(e: AuditEventView): string | null {
  const m = meta(e);
  if (e.action.startsWith("app.sso.")) {
    const entry: Pick<SsoAuditEntry, "event" | "before" | "after"> & Partial<Pick<SsoAuditEntry, "appId" | "appName">> = {
      event: e.action.slice("app.sso.".length) as SsoAuditEntry["event"],
      before: (m.before ?? null) as SsoAuditEntry["before"],
      after: (m.after ?? null) as SsoAuditEntry["after"],
      appId: e.target?.id ?? null,
      appName: e.target?.label ?? null,
    };
    return ssoChangeSummary(entry);
  }
  // Approvals already say who decided what; the other connector events carry their specifics in `detail`.
  if (e.action.startsWith("connector.") && e.action !== "connector.call" && e.action !== "connector.approval") return text(m.detail);
  if (e.action.startsWith("integration.grant.")) {
    const task = text(m.task);
    return task ? `For: ${task}` : null;
  }
  if (e.action === "automation_key.used") {
    const method = text(m.method);
    const path = text(m.path);
    const status = num(m.status);
    return method && path ? `${method} ${path}${status !== null ? ` → ${status}` : ""}` : null;
  }
  return null;
}
